import { CLIENT_AUTHENTICATION_SERVICE, ClientAuthenticationService } from "@cervval.js/client-auth";
import { LoginData } from "@cervval.js/common-auth/src/loginData.model";
import { Injectable } from "@cervval.js/common/src/dependencies/injectable";
import { injector } from "@cervval.js/common/src/dependencies/injector";
import { BookingCalendar, BookingCommonUser, BookingException, BookingProduct, BookingProfessional, BookingProfessionalCompany, BookingSession, BookingSharing, BookingUser, BookingUserSession, BookingWeek } from "@ehealth_tools/common-booking/src/data/booking.dto";
import { AbstractCommonBookingService, BOOKING_SERVICE } from "@ehealth_tools/common-booking/src/services/abstractCommonBooking.service";
import { BaseElementTypes } from "@ehealth_tools/common/src/data/dto/base.dto";

import { ClientActionCacheService } from "@cervval.js/client-action/src/services/client.action.cache.service";
import { ClientActionService } from "@cervval.js/client-action/src/services/client.action.service";
import { ClientRouteOptions, TypedClientAjaxProxy } from "@cervval.js/client-html5-spa/routes";
import { ACTION_SERVICE } from "@cervval.js/common-action/src/services/action.service";
import { ACTION_CACHE_SERVICE } from "@cervval.js/common-action/src/services/cache.service";
import { DTO_MODEL_SERVICE, DTOModelService } from "@cervval.js/common-dto/src/dtoModel.service";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { getLogConsole } from "@cervval.js/common/src/services/log.service";
import { getProp } from "@cervval.js/common/src/tools/any.tools";
import { UnixTimestamp } from "@cervval.js/common/src/tools/timestamps.tools.definitions";
import { anyReallyAny } from "@cervval.js/common/tools";
import { isProfessional } from "@ehealth_tools/common-booking/src/data/booking.tools";
import { AuthBookingRoutes, DeleteUserSessionReturnType } from "@ehealth_tools/common-booking/src/routes/authBooking.routes";
import { LatLng, MapAddress } from "@ehealth_tools/common-map/src/map.definitions";
import { FieldTypes, ID } from "@ehealth_tools/common/src/data/propertyTypes/property.types";
/**
 * Service for booking
 */
@Injectable(BOOKING_SERVICE)
export abstract class AbstractClientBookingService<
    ElementTypes extends BaseElementTypes = BaseElementTypes,
    AppUser extends BookingCommonUser = BookingCommonUser,
    AppProfessionalCompany extends BookingProfessionalCompany = BookingProfessionalCompany,
    AppLoginData extends LoginData<BookingCommonUser> = LoginData<BookingUser>
> extends AbstractCommonBookingService<ElementTypes, AppUser, AppProfessionalCompany, false> {

    @Injected(ACTION_SERVICE)
    public readonly actionService!: ClientActionService<ElementTypes, unknown>;

    @Injected(ACTION_CACHE_SERVICE)
    private _cacheService!: ClientActionCacheService<ElementTypes, unknown>;

    @Injected(DTO_MODEL_SERVICE)
    protected readonly _dtoModelService!: DTOModelService<ElementTypes, FieldTypes>;

    @TypedClientAjaxProxy()
    public readonly authBookingRoutes!: AuthBookingRoutes<ClientRouteOptions>;

    /** The current professional to interact with (eg. booking) */
    public userToInteractWith: BookingCommonUser | undefined;
    /** */
    public setUserToInteractWithById(id: ID | undefined): void {
        if (id == null) {
            this.userToInteractWith = undefined;
            return;
        }
        const pro = this.getById<BookingCommonUser>("bookingUsers", id);
        this.userToInteractWith = pro;
    }

    /** The date being (proposed at least) to book */
    public bookingDate: UnixTimestamp | undefined;
    /** The product being booked */
    public bookingProduct: BookingProduct | undefined;
    /** Current session user */
    private _currentSessionUser: BookingUserSession | undefined;

    /**
     * Set current session user => customer
     * @param user 
     */
    public setCurrentSessionUser(user: BookingUserSession): void {
        this._currentSessionUser = user;
    }

    /**
     * Get current session user => customer
     * @returns 
     */
    public getCurrentSessionUser(): BookingUserSession | undefined {
        return this._currentSessionUser;
    }

    public setBookingProductById(id: ID | undefined): void {
        if (id == null) { this.bookingProduct = undefined; return; }
        const product = this.getById<BookingProduct>("bookingProducts", id);
        this.bookingProduct = product;
    }

    /** */
    public override async book(p: { professionalId: ID, customerIds: ID[], productId: ID, date: UnixTimestamp }, options?: { price?: number, duration?: number, description?: string }): Promise<{ session: BookingSession, userSessions: BookingUserSession[] }> {
        const result = await this.authBookingRoutes.book({ p, options });
        this._cacheService.getDTOCacheRepository(this.tables.bookingSessions).push(result.session as anyReallyAny);
        for (const userSession of result.userSessions) {
            this._cacheService.getDTOCacheRepository(this.tables.bookingUserSessions).push(userSession as anyReallyAny);
        }
        return result;
    }

    public abstract get googleMapToken(): string;
    public abstract get googleMapUid(): string;

    /** Build booking session label */
    public abstract getSessionLabel(session: BookingSession): string;
    /** Build booking session tooltip if needed */
    public abstract getSessionTooltip(session: BookingSession): string | undefined;

    /** Get the current user -- userDTO is the reference stored in the cache */
    public getCurrentUser(): BookingCommonUser {
        const auth = injector.find<ClientAuthenticationService<BookingCommonUser, AppLoginData>>(CLIENT_AUTHENTICATION_SERVICE);
        if (auth == null) { throw new Error("Client Authentication is not started yet. Is the current user logged in ?"); }
        return this.getById("bookingUsers", auth.getLoginData().user.id); // ensure that the reference is the same
    }

    /** */
    public isCurrentUserProfessional(): boolean {
        return isProfessional(this.getCurrentUser());
    }

    /**
     * Retrieve all relations of a current user
     * @returns 
     */
    public getRelationsOfCurrentUser(): BookingSharing[] {
        const relations = this.getData<BookingSharing>("bookingSharing");

        const clientRelations = relations.filter((relation) => relation.fromId === this.getCurrentUser().id);
        return clientRelations;
    }

    /**
     * Retrieve all professionals related to a current user
     * @returns 
     */
    public getProfessionalsOfCurrentUser(): BookingProfessional[] {
        const users = this.getData<BookingCommonUser>("bookingUsers") as BookingCommonUser[];
        const relations = this.getRelationsOfCurrentUser();
        const relationSet: Set<number> = new Set();
        for (const relation of relations) {
            relationSet.add(relation.toId);
        }

        const professionals = users.filter((user) => relationSet.has(user.id) && isProfessional(user)) as BookingProfessional[];
        return professionals;
    }

    /**
     * Get the current user calendar and data
     */
    public getCurrentUserCalendarData(): { calendar: BookingCalendar, defaultWeek: BookingWeek, exceptionDataList: { exception: BookingException, week: BookingWeek }[] } {
        const calendarId = this.getCurrentUser().calendarId;
        const calendar = this.getById<BookingCalendar>("bookingCalendars", calendarId);
        const defaultWeek = this.getById<BookingWeek>("bookingWeeks", calendar.defaultWeekId);
        const exceptions = this.getData<BookingException>("bookingExceptions").filter(v => v.calendarId === calendarId);
        const exceptionDataList: ReturnType<typeof this.getCurrentUserCalendarData>["exceptionDataList"] = [];
        for (const exception of exceptions) {
            const week = this.getById<BookingWeek>("bookingWeeks", exception.weekId);
            exceptionDataList.push({
                exception,
                week
            });
        }

        return { calendar, defaultWeek, exceptionDataList };
    }

    /** Get the products data and filter the deleted out if asked and if there is a deletion marker in the dtoModelService */
    public getFilteredProducts(filterDeleted?: boolean): BookingProduct[] {
        const products = this.getData<BookingProduct>("bookingProducts");
        if (filterDeleted) {
            // We retrieve the dtoModelService's config by using the table params in order to have the possibly overridden dto's properties 
            const deletionMarkerField = this._dtoModelService.getTableArchivedField(this._params.tables.bookingProducts) as string | undefined;
            if (deletionMarkerField != null) {
                const filteredProducts: BookingProduct[] = [];
                for (const product of products) {
                    if (getProp(product, deletionMarkerField) == null) {
                        filteredProducts.push(product);
                    }
                }
                return filteredProducts;
            }
        }
        return products;
    }
    /** Build static url for the image */
    public abstract getSnippetURL(user: BookingCommonUser, prefix?: string): string | null;

    /** Called to save location extracted from google map */
    public saveLocation(dto: MapAddress & { id: ID }, localization: LatLng): Promise<void> {
        getLogConsole().info("Location save is not implemented");
        dto.lat = localization.lat;
        dto.lng = localization.lng;
        return Promise.resolve();
    }

    /** If logged user is required, method to show login form */
    public abstract showLogin(): void;

    /** */
    public async deleteUserSession(userSessionId: ID): Promise<DeleteUserSessionReturnType> {
        const result = await this.authBookingRoutes.deleteUserSession({ userSessionId });
        // Update local cache
        for (const deletedUserSessionId of result.userSessionIds) {
            const localDto = this._cacheService.getDTOCacheRepository(this.tables.bookingUserSessions).findDTOById(deletedUserSessionId);
            if (localDto) {
                this._cacheService.getDTOCacheRepository(this.tables.bookingUserSessions).remove(localDto);
            }
        }
        if (result.sessionId) {
            const localSessionDto = this._cacheService.getDTOCacheRepository(this.tables.bookingSessions).findDTOById(result.sessionId);
            if (localSessionDto) {
                this._cacheService.getDTOCacheRepository(this.tables.bookingSessions).remove(localSessionDto as anyReallyAny);
            }
        }


        return result;
    }
}